// Runtime config from window (set by public/config.js loaded at startup)
const runtimeConfig = window.__RUNTIME_CONFIG__ || {};

// Helper to check if a value is an unresolved placeholder (used in production containers).
// Only checks for the literal marker — "" is a real, intentional value, not a placeholder.
const isPlaceholder = (value) => {
    return typeof value !== 'string' || value.includes('PLACEHOLDER');
};

// Use runtime config only if it's not a placeholder, otherwise fall back to env vars.
// Uses `!== undefined` (not `||`) so an intentionally blank "" is preserved instead of
// being overwritten by defaultValue.
const getValue = (runtimeValue, envValue, defaultValue) => {
    if (!isPlaceholder(runtimeValue)) {
        return runtimeValue;
    }
    return envValue !== undefined ? envValue : defaultValue;
};

// Boolean feature flags: on unless explicitly switched off. Accepts the usual
// spellings of "off" so REACT_APP_X=false behaves the same as REACT_APP_X=0.
const isFlagOff = (value) => ["0", "false", "no", "off"].includes(String(value).trim().toLowerCase());
// For flags that default OFF: only an explicit yes turns them on, so "" or a typo stays off.
const isFlagOn = (value) => ["1", "true", "yes", "on"].includes(String(value).trim().toLowerCase());

const config = {
    // Still required, and not just by the legacy Pattern-B call sites. utils/
    // oauthPopup.js derives the origin it will accept postMessage from out of
    // this value, and the OAuth callback still lands on discover's origin (not
    // the chat backend's) — so dropping this as "migrated away" silently falls back to
    // http://localhost:9380 and breaks the integrations popup in every
    // environment where discover is not on localhost.
    serviceUrl: getValue(
        runtimeConfig.serviceUrl,
        process.env.REACT_APP_SERVICE_URL,
        "http://localhost:9380/api/v1"
    ),
    baseMediaUrl: getValue(
        runtimeConfig.baseMediaUrl,
        process.env.REACT_APP_BASE_MEDIA_URL,
        "http://discover-media.elastiq.ai/"
    ),
    canvasApiUrl: getValue(
        runtimeConfig.canvasApiUrl,
        process.env.REACT_APP_CANVAS_API_URL,
        "http://localhost:9380/v1/canvas/canvas_info"
    ),
    // Discover Chat's own backend (api/) — shaped the same way as serviceUrl
    // so the same `.replace(/\/api\/v1\/?$/, '')` trick used for origin-based
    // discover calls (e.g. document preview URLs) works unchanged when pointed
    // at it instead. Every call site routed through here sends the real
    // Keycloak access token (via getAccessToken()) — the static apiKey that
    // used to live here has been fully migrated off and removed; the backend is
    // now the only thing that holds that secret, server-side.
    apiUrl: getValue(
        runtimeConfig.apiUrl,
        process.env.REACT_APP_API_URL,
        "http://localhost:4000/api/v1"
    ),

    env: getValue(
        runtimeConfig.env,
        process.env.REACT_APP_ENV,
        "dev"
    ),

    // White-label branding
    brandName: getValue(
        runtimeConfig.brandName,
        process.env.REACT_APP_BRAND_NAME,
        "Discover"
    ),
    brandTagline: getValue(
        runtimeConfig.brandTagline,
        process.env.REACT_APP_BRAND_TAGLINE,
        "by elastiq.ai"
    ),
    brandShortName: getValue(
        runtimeConfig.brandShortName,
        process.env.REACT_APP_BRAND_SHORT_NAME,
        "elastiq.ai"
    ),
    brandFullName: getValue(
        runtimeConfig.brandFullName,
        process.env.REACT_APP_BRAND_FULL_NAME,
        "Elastiq Discover"
    ),
    brandLogoUrl: getValue(
        runtimeConfig.brandLogoUrl,
        process.env.REACT_APP_BRAND_LOGO_URL,
        "/discoverLogo.png"
    ),
    // Multiplier on top of each spot's base logo height — tune per client when
    // a logo file has more internal padding than discover's and renders small.
    brandLogoScale: parseFloat(getValue(
        runtimeConfig.brandLogoScale,
        process.env.REACT_APP_BRAND_LOGO_SCALE,
        "1"
    )) || 1,

    // Fast/Thinking analysis mode picker in the chat input. Default on;
    // set to "0" to hide the control from the UI entirely.
    fastThinkingModeEnabled: !isFlagOff(getValue(
        runtimeConfig.fastThinkingModeEnabled,
        process.env.REACT_APP_FAST_THINKING_MODE_ENABLED,
        "1"
    )),

    // Completions V2 for chat messages. Default off; set to "1" to send messages to
    // /agents/{id}/completions/v2, which applies the agent's LLM Schema answer format. Turn it
    // on only where discover serves V2 (and any gateway in front allows the path); set it
    // back to "0" to return to /completions without a code change.
    completionsV2Enabled: isFlagOn(getValue(
        runtimeConfig.completionsV2Enabled,
        process.env.REACT_APP_COMPLETIONS_V2_ENABLED,
        "0"
    )),

    // Session sharing (share links on a chat, the "Shared links" dialog and the
    // link badge in the sidebar). Default on; set to "0"/"false" to hide every
    // share control from the UI. Frontend-only: the /s/:token viewer route and
    // the backend share endpoints are untouched, so links already handed out
    // keep working until they are revoked or expire.
    sessionShareEnabled: !isFlagOff(getValue(
        runtimeConfig.sessionShareEnabled,
        process.env.REACT_APP_SESSION_SHARE_ENABLED,
        "1"
    )),

    // Keycloak SSO Configuration
    keycloak: {
        serverUrl: getValue(
            runtimeConfig.keycloakServerUrl,
            process.env.REACT_APP_KC_SERVER_URL,
            "https://auth.elastiq.app"
        ),
        realm: getValue(
            runtimeConfig.keycloakRealm,
            process.env.REACT_APP_KC_REALM,
            "discover_chat"
        ),
        clientId: getValue(
            runtimeConfig.keycloakClientId,
            process.env.REACT_APP_KC_CLIENT_ID,
            "discover-chat-app"
        ),
    }
};

export default config;
